import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import {
  GlyphWalletProvider,
  useNativeGlyphConnection,
} from "@use-glyph/sdk-react";
import { defineChain as defineViemChain, http } from "viem";
import { useAccount, useWalletClient } from "wagmi";
import { createThirdwebClient } from "thirdweb";
import {
  useActiveWallet,
  useConnect,
  useDisconnect,
} from "thirdweb/react";
import { viemAdapter } from "thirdweb/adapters/viem";
import {
  NS_BLOCK_EXPLORER_URL,
  NS_CHAIN,
  NS_CHAIN_ID,
  NS_CHAIN_NAME,
  NS_NATIVE_CURRENCY_NAME,
  NS_NATIVE_CURRENCY_SYMBOL,
  NS_RPC_URL,
} from "../config/chain";
import { logger } from "../utils/logger";

type NobodySausageGlyphContextValue = {
  connect: () => Promise<void> | void;
  disconnect: () => void;
  isConnected: boolean;
  isConnecting: boolean;
};

const defaultValue: NobodySausageGlyphContextValue = {
  connect: () => {},
  disconnect: () => {},
  isConnected: false,
  isConnecting: false,
};

const NobodySausageGlyphContext =
  createContext<NobodySausageGlyphContextValue>(defaultValue);

function NobodySausageGlyphInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const client = useMemo(
    () =>
      createThirdwebClient({
        clientId: process.env.REACT_APP_THIRDWEB_CLIENT_ID as string,
      }),
    [],
  );
  const { connect, disconnect: disconnectGlyph } = useNativeGlyphConnection();
  const { isConnected, isConnecting, address } = useAccount();
  const { data: walletClient } = useWalletClient();
  const activeWallet = useActiveWallet();
  const { disconnect: disconnectThirdweb } = useDisconnect();
  const lastBridgedAddressRef = useRef<string | null>(null);
  const bridgingAddressRef = useRef<string | null>(null);
  const { connect: connectThirdweb } = useConnect({
    client,
    accountAbstraction: {
      chain: NS_CHAIN,
      sponsorGas: true,
    },
  });
  const connectThirdwebRef = useRef(connectThirdweb);

  useEffect(() => {
    connectThirdwebRef.current = connectThirdweb;
  }, [connectThirdweb]);

  useEffect(() => {
    let cancelled = false;

    async function bridgeGlyphToThirdweb() {
      if (!isConnected || !walletClient || !address) {
        lastBridgedAddressRef.current = null;
        bridgingAddressRef.current = null;
        return;
      }

      // Skip only if this address is already bridged (success) or a bridge is
      // currently in flight for it. A *failed* attempt must be allowed to retry
      // when the effect re-runs (e.g. once walletClient becomes ready), so we
      // deliberately do not track "attempted" addresses here.
      if (
        lastBridgedAddressRef.current === address ||
        bridgingAddressRef.current === address
      ) {
        return;
      }

      try {
        bridgingAddressRef.current = address;

        logger.wallet("Bridging Glyph wallet into thirdweb", {
          address,
          glyphChainId: walletClient.chain?.id,
          chainId: NS_CHAIN.id,
          sponsorGas: true,
        });

        await connectThirdwebRef.current(async () => {
          const wallet = await viemAdapter.wallet.fromViem({
            // thirdweb bundles its own viem types, so we bridge through a narrow cast here
            walletClient: walletClient as any,
          });
          await wallet.connect({
            client,
            chain: NS_CHAIN,
          });
          return wallet;
        });

        if (cancelled) {
          return;
        }

        lastBridgedAddressRef.current = address;
        logger.wallet("Glyph wallet bridged into thirdweb", {
          address,
          glyphChainId: walletClient.chain?.id,
          chainId: NS_CHAIN.id,
          sponsorGas: true,
        });
      } catch (error) {
        logger.walletError("Failed to bridge Glyph wallet into thirdweb", error as Error, {
          address,
          chainId: NS_CHAIN.id,
        });
      } finally {
        if (bridgingAddressRef.current === address) {
          bridgingAddressRef.current = null;
        }
      }
    }

    bridgeGlyphToThirdweb();

    return () => {
      cancelled = true;
    };
  }, [address, client, isConnected, walletClient]);

  const value = useMemo<NobodySausageGlyphContextValue>(
    () => ({
      connect,
      disconnect: () => {
        if (activeWallet) {
          disconnectThirdweb(activeWallet);
        }
        disconnectGlyph();
      },
      isConnected,
      isConnecting,
    }),
    [
      activeWallet,
      connect,
      disconnectGlyph,
      disconnectThirdweb,
      isConnected,
      isConnecting,
    ],
  );

  return (
    <NobodySausageGlyphContext.Provider value={value}>
      {children}
    </NobodySausageGlyphContext.Provider>
  );
}

export function NobodySausageGlyphProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const glyphChain = useMemo(
    () =>
      defineViemChain({
        id: NS_CHAIN_ID,
        name: NS_CHAIN_NAME,
        nativeCurrency: {
          name: NS_NATIVE_CURRENCY_NAME,
          symbol: NS_NATIVE_CURRENCY_SYMBOL,
          decimals: 18,
        },
        rpcUrls: {
          default: {
            http: NS_RPC_URL ? [NS_RPC_URL] : [],
          },
        },
        blockExplorers: NS_BLOCK_EXPLORER_URL
          ? {
              default: {
                name: "default",
                url: NS_BLOCK_EXPLORER_URL,
              },
            }
          : undefined,
      }),
    [],
  );
  const glyphChains = useMemo(() => [glyphChain] as [typeof glyphChain], [glyphChain]);
  const glyphTransports = useMemo(
    () => ({
      [glyphChain.id]: http(NS_RPC_URL),
    }),
    [glyphChain],
  );

  return (
    <GlyphWalletProvider
      askForSignature={true}
      chains={glyphChains}
      transports={glyphTransports}
    >
      <NobodySausageGlyphInner>{children}</NobodySausageGlyphInner>
    </GlyphWalletProvider>
  );
}

export function useNobodySausageGlyph() {
  return useContext(NobodySausageGlyphContext);
}
